import { useEffect, useState } from "react";
import { api } from "./api";
import type { DocEntry, OrgId } from "../types";

type DevhubCounts = Record<string, Record<string, { docs: number; apiRef: number }>>;
// Pick the latest version (highest key) for an org's counts, so the sidebar shows
// a single version's totals — not the sum across every version.
function latestVersionCounts(counts: DevhubCounts, org: string): { docs: number; apiRef: number } {
  const byVer = counts[org] ?? {};
  const latest = Object.keys(byVer).sort().pop();
  return latest ? byVer[latest] : { docs: 0, apiRef: 0 };
}

// Per-org counts for the sidebar's org-scoped views: repositories, system docs,
// the knowledge graph, and agents. Docs are fetched once (the listing is global)
// and counted by their top-level folder; the rest are re-fetched whenever the org
// changes. Each value is `undefined` until its fetch resolves.
//
// The repos, knowledge-graph and agents surfaces are Omni Hub admin-only (the API
// 403s everyone else), so those counts are fetched only when `admin` — non-admins
// never see the nav items and shouldn't fire a denied call per page load.
export interface OrgCounts {
  repos?: number;
  docs?: number;
  devhub?: number;
  apiRef?: number;
  /** Every page in the org's knowledge graph, of every type. */
  graph?: number;
  agents?: number;
}

export function useOrgCounts(org: OrgId, admin: boolean): OrgCounts {
  const [docsAll, setDocsAll] = useState<DocEntry[] | null>(null);
  const [devhubCounts, setDevhubCounts] = useState<DevhubCounts | null>(null);
  const [repos, setRepos] = useState<number | undefined>(undefined);
  const [graph, setGraph] = useState<number | undefined>(undefined);
  const [agents, setAgents] = useState<number | undefined>(undefined);

  // Docs listing + DevHub counts are org-agnostic — fetch each once. The DevHub
  // count comes from the lightweight structure endpoint (per org → version), so the
  // sidebar never downloads the whole multi-version page corpus just to show a badge.
  useEffect(() => {
    let cancelled = false;
    api.getDocs().then((d) => { if (!cancelled) setDocsAll(Array.isArray(d) ? d : []); }).catch(() => {});
    api.getDevhubStructure().then((s) => { if (!cancelled) setDevhubCounts(s?.pageCounts ?? {}); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Repos, the knowledge graph and agents are per-org, and admin-only.
  useEffect(() => {
    let cancelled = false;
    setRepos(undefined);
    setGraph(undefined);
    setAgents(undefined);
    if (!admin) return;
    api.getRepos(org)
      .then((r) => { if (!cancelled) setRepos(Array.isArray(r?.repos) ? r.repos.length : 0); })
      .catch(() => { if (!cancelled) setRepos(0); });
    // The graph total comes from the corpus's own index, so this is one small
    // request rather than a listing of ~1,400 pages. An org with no corpus 404s,
    // which lands on 0 — the same thing its tab says, rather than an empty badge.
    api.getGraphSummary(org)
      .then((s) => { if (!cancelled) setGraph(s?.stats?.entries ?? 0); })
      .catch(() => { if (!cancelled) setGraph(0); });
    api.getAgentRepos(org)
      .then((r) => { if (!cancelled) setAgents(typeof r?.total === "number" ? r.total : (r?.repos?.length ?? 0)); })
      .catch(() => { if (!cancelled) setAgents(0); });
    return () => { cancelled = true; };
  }, [org, admin]);

  const docs = docsAll ? docsAll.filter((d) => d.dir === org).length : undefined;
  // DevHub Docs / API reference counts for the latest version only (not summed
  // across versions). API reference exists only for incode today.
  const devhub = devhubCounts ? latestVersionCounts(devhubCounts, org).docs : undefined;
  const apiRef = devhubCounts ? latestVersionCounts(devhubCounts, org).apiRef : undefined;
  return { repos, docs, devhub, apiRef, graph, agents };
}
